import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Paper, Tab, Tabs } from '@esgian/esgianui';
import { useSearchParams } from 'react-router-dom';
import VesselEmissionsSection from './SubSections/VesselEmissionsSection/VesselEmissionsSection';
import VesselOverviewSection from '@components/Sections/ShipAnalytics/VesselProfile/SubSections/VesselOverviewSection';
import VesselActivitySection from '@components/Sections/ShipAnalytics/VesselProfile/SubSections/VesselActivitySection';
import VesselCiiRatingSection from '@components/Sections/ShipAnalytics/VesselProfile/SubSections/VesselCiiRatingSection';
import { TextWithTooltipIcon } from '@components';
import { INSUFFICIENT_DATA_FOR_EMISSIONS } from '@constants/tooltips';
import { useQuery } from '@tanstack/react-query';
import { getLatestPortCalls, getVesselCiiRatingData, getVesselPercentageCo2Emission } from '@api';
import { DEFAULT_NUM_PORT_CALLS, GEOGRAPHICAL_TYPES } from '@constants';
import { useFutureScheduleAvailability } from '@hooks/useFutureScheduleAvailability';
import { REGION_DATA as params, REGION_DATA as segment } from '../../../../dtos/geography';
import FutureScheduleSection from '@components/Sections/ShipAnalytics/FutureScheduleSection';
import { useSegment } from '@hooks';

function VesselProfileSection({ vesselName, imo, setPageCount }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabSection, setTabSection] = useState(parseInt(searchParams.get('section') || 1));
  const [isFutureScheduleAvailable, setIsFutureScheduleAvailable] = useState(true);
  const { isRoRo } = useSegment();

  const isMapFullscreen = searchParams.get('overviewMapFullscreen') === 'true';

  const vesselEmissionsDataQuery = useQuery({
    queryKey: ['vesselEmissions', imo],
    enabled: !!imo,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getVesselPercentageCo2Emission(queryKey[1], signal)
        .then((res) => {
          return res;
        })
        .catch(() => {
          return null;
        });
    }
  });

  const vesselCiiRatingQuery = useQuery({
    queryKey: ['vesselCiiRating', 'monthly', imo],
    enabled: !!imo,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getVesselCiiRatingData(queryKey[2], queryKey[1], signal)
        .then((result) => result)
        .catch(() => null);
    }
  });

  const portCallsQuery = useQuery({
    queryKey: ['portCalls', imo],
    enabled: !!imo && [1, 2].includes(tabSection),
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getLatestPortCalls(
        {
          geographicalType: GEOGRAPHICAL_TYPES.ALL,
          numberOfPortCalls: DEFAULT_NUM_PORT_CALLS,
          imos: [queryKey[1]]
        },
        signal
      )
        .then((result) => result)
        .catch(() => null);
    }
  });

  const hasCiiData = useMemo(() => {
    return vesselCiiRatingQuery.data?.emissionExistsFlag ?? false;
  }, [vesselCiiRatingQuery.data]);

  const handleSectionChange = (_, val) => {
    if (!vesselEmissionsDataQuery.data && val === 3) {
      return;
    }
    if (!hasCiiData && val === 4) {
      return;
    }
    if (!isFutureScheduleAvailable && val === 5) {
      return;
    }
    setTabSection(val);
    searchParams.set('section', val);
    setSearchParams(searchParams);
    setPageCount((prev) => prev - 1);
  };

  const futurePortCallsQuery = useFutureScheduleAvailability({
    imoList: [imo],
    segmentId: segment.id,
    entityId: params.id,
    setIsAvailable: setIsFutureScheduleAvailable
  });

  // If map fullscreen is active, just return VesselOverviewSection (which handles full screen)
  if (isMapFullscreen) {
    return <VesselOverviewSection portCallsQuery={portCallsQuery} />;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper>
          <Tabs value={tabSection} onChange={handleSectionChange} variant="scrollable">
            <Tab value={1} label="Overview" id="vessel-overview-tab" />
            <Tab value={2} label="Activity" id="vessel-activity-tab" />
            <Tab
              sx={{ cursor: vesselEmissionsDataQuery.data ? 'pointer' : 'default' }}
              disableRipple={!vesselEmissionsDataQuery.data}
              id={'vessel-co2-emissions-tab'}
              value={3}
              label={
                !vesselEmissionsDataQuery.isFetching && !vesselEmissionsDataQuery.data ? (
                  <TextWithTooltipIcon
                    color={'text.secondary'}
                    label={'CO₂ EMISSIONS'}
                    tooltipText={INSUFFICIENT_DATA_FOR_EMISSIONS}
                  />
                ) : (
                  'CO₂ EMISSIONS'
                )
              }
            />
            <Tab
              sx={{ cursor: hasCiiData ? 'pointer' : 'default' }}
              disableRipple={!hasCiiData}
              value={4}
              id="vessel-cii-rating-tab"
              label={
                !hasCiiData ? (
                  <TextWithTooltipIcon
                    color={'text.secondary'}
                    label={'CII RATING'}
                    tooltipText={'Insufficient data to compute CII'}
                  />
                ) : (
                  'Cii rating'
                )
              }
            />
            {isRoRo && (
              <Tab
                value={5}
                label={
                  isFutureScheduleAvailable ? (
                    'FUTURE SCHEDULE'
                  ) : (
                    <TextWithTooltipIcon
                      color={'text.secondary'}
                      label={'FUTURE SCHEDULE'}
                      tooltipText={'Schedule not available'}
                    />
                  )
                }
                sx={{ cursor: isFutureScheduleAvailable ? 'pointer' : 'default' }}
                disableRipple={!isFutureScheduleAvailable}
              />
            )}
          </Tabs>
        </Paper>
      </Grid>
      {tabSection === 1 && <VesselOverviewSection portCallsQuery={portCallsQuery} />}
      {tabSection === 2 && (
        <VesselActivitySection portCallsQuery={portCallsQuery} vesselName={vesselName} />
      )}
      {tabSection === 3 && (
        <VesselEmissionsSection
          percentageChangeQuery={vesselEmissionsDataQuery}
          vesselName={vesselName}
        />
      )}
      {tabSection === 4 && <VesselCiiRatingSection vesselName={vesselName} />}
      {tabSection === 5 && <FutureScheduleSection futurePortCallsQuery={futurePortCallsQuery} />}
    </Grid>
  );
}

VesselProfileSection.propTypes = {
  setPageCount: PropTypes.func.isRequired,
  imo: PropTypes.string,
  vesselName: PropTypes.string
};

export default VesselProfileSection;
